import { useTranslation } from 'react-i18next';
import {Stack, Text, Card, Alert, Button, Title, Badge} from '@efast_public/fjd-component-library';
import './DashboardPage.css';
import { useQuery } from '@tanstack/react-query';
import { GovosApiRoutes } from '../../api/govos-api';
import { Product, ProductResponse } from '../../api/govos.types';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getEnvironmentVar } from '../../shared/utils/getEnvironmentVar';

interface ModuleCardProps {
  product: Product;
}

function ModuleCard({ product }: ModuleCardProps) {
  const isEnabled = !!product.url;

  const handleClick = () => {
    if (isEnabled) {
      window.open(product.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleClick();
    }
  };

  return (
    <Card style={{ opacity: isEnabled ? 1 : 0.4 }} elevation>
      <Card.ActionArea
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={isEnabled ? 'link' : 'presentation'}
        tabIndex={isEnabled ? 0 : -1}
        aria-label={isEnabled ? `${product.name} - ${product.desc}` : undefined}
        aria-disabled={!isEnabled}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '24px 16px',
          cursor: isEnabled ? 'pointer' : 'default',
          textAlign: 'center',
          minHeight: '140px'
        }}
      >
        <div style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={product.icon}
            alt=""
            aria-hidden="true"
            width={48}
            height={48}
          />
        </div>
        <Text size="s" strong style={{ marginTop: '4px' }}>
          {product.name}
        </Text>
        <Text size="s" style={{ color: '#666' }}>
          {product.desc}
        </Text>
      </Card.ActionArea>
    </Card>
  );
}

export function DashboardPage() {
  const { t } = useTranslation('mc');
  const { data: productResponse } = useQuery<ProductResponse>({
    queryKey: [GovosApiRoutes.products()]
  });

  const isDashboardPreviewEnabled = getEnvironmentVar('REACT_APP_FEATURE_TOGGLE') === 'true';

  return (
    <div className="dashboard-page">
      <Stack spacing="5xl">
        <Stack>
          <Text size="xxl" strong>
            {t('dashboardPage.title')}
          </Text>
        </Stack>

        {/* Notifications Section */}
        {isDashboardPreviewEnabled && <Stack spacing="m">
          <Stack direction="row" spacing="s" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Title level={2} visualLevel={5}>
              Meldungen
            </Title>
            <Badge variant="info" size="sm">
              Demnächst verfügbar
            </Badge>
          </Stack>
          <Alert
            variant="warning"
            style={{ opacity: 0.6, pointerEvents: 'none' }}
            message={
              <Stack spacing="xs">
                <Text size="s" style={{ marginBottom: '8px' }}>
                  <strong>32 neue öffentliche Online-Dienste verfügbar</strong> — Die Community hat in diesem Monat 32 neue öffentliche Online-Dienste bereitgestellt, die Sie direkt nutzen können.
                </Text>
                <Button style={{ width: '200px'}} variant="primary-text" size={"sm"} onClick={() => console.log("Navigate to updates")}>
                  Aktualisierungen ansehen
                </Button>
              </Stack>
            }
          />

          <Alert
            variant="info"
            style={{ opacity: 0.6, pointerEvents: 'none' }}
            message={
              <Stack spacing={"xs"}>
                <Text size="s">
                  <strong>17 Online-Dienste mit aktualisierten Versionen</strong> — Die Community hat für 17 Online-Dienste neue Versionen mit Verbesserungen und neuen Funktionen veröffentlicht.
                </Text>
                <Button  style={{ width: '200px'}} variant="primary-text" size={"sm"} onClick={() => console.log("Navigate to updates")}>
                  Aktualisierungen ansehen
                </Button>
              </Stack>
            }
          />
        </Stack>}

        {/* Modules Section */}
        <Stack spacing="m">
          <Stack direction="row" spacing="s" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Title level={2} visualLevel={5}>
              Module
            </Title>
          </Stack>
          <div className="dashboard-page__grid">
            {productResponse?.userInfo?.products
              ?.filter((p) => p.identifier !== 'efast')
              .map((product) => (
                <ModuleCard key={product.name} product={product} />
              ))}
          </div>
        </Stack>

        {/* EFAST-Link Monitor Section */}
        {isDashboardPreviewEnabled && <Stack spacing="m">
          <Stack direction="row" spacing="s" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Title level={2} visualLevel={5}>
              EFAST-Link Monitor
            </Title>
            <Badge variant="info" size="sm">
              Demnächst verfügbar
            </Badge>
          </Stack>

          <div className="dashboard-page__grid">
            <Card elevation={false} style={{ opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="xs" style={{ padding: '16px' }}>
                <Text size="xs" style={{ color: '#999', textTransform: 'uppercase' }}>⚠ Fehlerhaft</Text>
                <Text size="xxl" strong style={{ color: '#ff6b6b' }}>3 <Text size="s" style={{ color: '#999' }}>EFAST-Link →</Text></Text>
              </Stack>
            </Card>

            <Card elevation={false} style={{ opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="xs" style={{ padding: '16px' }}>
                <Text size="xs" style={{ color: '#999', textTransform: 'uppercase' }}>✓ Erfolgreich</Text>
                <Text size="xxl" strong style={{ color: '#51cf66' }}>1.844 <Text size="s" style={{ color: '#999' }}>Erfolgreich</Text></Text>
              </Stack>
            </Card>

            <Card elevation={false} style={{ opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="xs" style={{ padding: '16px' }}>
                <Text size="xs" style={{ color: '#999', textTransform: 'uppercase' }}>↓ Eingänge gesamt</Text>
                <Text size="xxl" strong style={{ color: '#4dabf7' }}>1.847 <Text size="s" style={{ color: '#999' }}>Letzten 4 Wochen</Text></Text>
              </Stack>
            </Card>

            <Card elevation={false} style={{ opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="xs" style={{ padding: '16px' }}>
                <Stack direction="row" spacing="s" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text size="xs" style={{ color: '#999', textTransform: 'uppercase' }}>📋 Workflows</Text>
                  <Text size="xs" style={{ color: '#666' }}>VORSCHAU</Text>
                </Stack>
                <Text size="xxl" strong>7 <Text size="s" style={{ color: '#999' }}>mit Eingängen</Text></Text>
              </Stack>
            </Card>
          </div>

          <Stack direction="row" spacing="m">
            <Card elevation={false} style={{ flex: 1, opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="s" style={{ padding: '16px' }}>
                <Text size="s" strong>Eingänge nach Workflow</Text>
                <Text size="xs" style={{ color: '#999' }}>Letzten 6 Monate</Text>
                <Stack spacing="xs" style={{ marginTop: '8px' }}>
                  <Stack direction="row" spacing="s" style={{ justifyContent: 'space-between' }}>
                    <Text size="xs">Führungszeugnis</Text>
                    <Stack direction="row" spacing="m">
                      <Text size="xs">413</Text>
                      <Text size="xs">413</Text>
                      <Text size="xs" strong>100,0 %</Text>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing="s" style={{ justifyContent: 'space-between' }}>
                    <Text size="xs">Führungszeugnis</Text>
                    <Stack direction="row" spacing="m">
                      <Text size="xs">387</Text>
                      <Text size="xs">387</Text>
                      <Text size="xs" strong>100,0 %</Text>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing="s" style={{ justifyContent: 'space-between' }}>
                    <Text size="xs">Meldebescheinigung</Text>
                    <Stack direction="row" spacing="m">
                      <Text size="xs">258</Text>
                      <Text size="xs">255</Text>
                      <Text size="xs" strong style={{ color: '#ff6b6b' }}>98,8 %</Text>
                    </Stack>
                  </Stack>
                </Stack>
                <Text size="xs" style={{ color: '#0066cc', cursor: 'pointer', marginTop: '8px' }}>Mehr anzeigen (3 weitere) +</Text>
              </Stack>
            </Card>

            <Card elevation={false} style={{ flex: 1, opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="s" style={{ padding: '16px' }}>
                <Text size="s" strong>Monatliche Eingänge</Text>
                <Text size="xs" style={{ color: '#999' }}>Letzten 6 Monate</Text>
                <div style={{ height: '120px', marginTop: '8px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      { month: 'Jan', value: 280 },
                      { month: 'Feb', value: 250 },
                      { month: 'Mär', value: 340 },
                      { month: 'Apr', value: 350 },
                      { month: 'Mai', value: 240 },
                      { month: 'Jun', value: 310 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#4dabf7" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Stack>
            </Card>
          </Stack>
        </Stack>}

        {/* Statistics Section */}
        {isDashboardPreviewEnabled && <Stack spacing="m">
          <Stack direction="row" spacing="s" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Title level={2} visualLevel={5}>
              Statistik – Aufrufe
            </Title>
            <Badge variant="info" size="sm">
              Demnächst verfügbar
            </Badge>
          </Stack>

          <div className="dashboard-page__grid">
            <Card elevation={false} style={{ opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="xs" style={{ padding: '16px' }}>
                <Text size="xs" style={{ color: '#999', textTransform: 'uppercase' }}>≡ Online-Dienste gesamt</Text>
                <Text size="xxl" strong>57</Text>
              </Stack>
            </Card>

            <Card elevation={false} style={{ opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="xs" style={{ padding: '16px' }}>
                <Text size="xs" style={{ color: '#999', textTransform: 'uppercase' }}>✓ Aktive Online-Dienste</Text>
                <Text size="xxl" strong style={{ color: '#51cf66' }}>53</Text>
              </Stack>
            </Card>

            <Card elevation={false} style={{ opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="xs" style={{ padding: '16px' }}>
                <Text size="xs" style={{ color: '#999', textTransform: 'uppercase' }}>— Inaktive Online-Dienste</Text>
                <Text size="xxl" strong style={{ color: '#ffd43b' }}>4</Text>
              </Stack>
            </Card>

            <Card elevation={false} style={{ opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="xs" style={{ padding: '16px' }}>
                <Stack direction="row" spacing="s" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text size="xs" style={{ color: '#999', textTransform: 'uppercase' }}>↑ Aufrufe gesamt</Text>
                  <Text size="xs" style={{ color: '#666' }}>VORSCHAU</Text>
                </Stack>
                <Text size="xxl" strong style={{ color: '#4dabf7' }}>1.423</Text>
              </Stack>
            </Card>
          </div>

          <Stack direction="row" spacing="m">
            <Card elevation={false} style={{ flex: 1, opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="s" style={{ padding: '16px' }}>
                <Text size="s" strong>Top 10 aufgerufener Online-Dienste</Text>
                <Text size="xs" style={{ color: '#999' }}>Letzten 6 Monate</Text>
                <Stack spacing="xs" style={{ marginTop: '8px' }}>
                  <Stack direction="row" spacing="s" style={{ justifyContent: 'space-between' }}>
                    <Text size="xs">1 Führungszeugnis</Text>
                    <Stack direction="row" spacing="m">
                      <Text size="xs">245</Text>
                      <Text size="xs">17,2 %</Text>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing="s" style={{ justifyContent: 'space-between' }}>
                    <Text size="xs">2 Führungszeugnis</Text>
                    <Stack direction="row" spacing="m">
                      <Text size="xs">198</Text>
                      <Text size="xs">13,9 %</Text>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing="s" style={{ justifyContent: 'space-between' }}>
                    <Text size="xs">3 Meldebescheinigung</Text>
                    <Stack direction="row" spacing="m">
                      <Text size="xs">—</Text>
                      <Text size="xs">—</Text>
                    </Stack>
                  </Stack>
                </Stack>
                <Text size="xs" style={{ color: '#0066cc', cursor: 'pointer', marginTop: '8px' }}>Mehr anzeigen (8 weitere) +</Text>
              </Stack>
            </Card>

            <Card elevation={false} style={{ flex: 1, opacity: 0.6, pointerEvents: 'none' }}>
              <Stack spacing="s" style={{ padding: '16px' }}>
                <Text size="s" strong>Monatliche Aufrufe der letzten 6 Monate</Text>
                <Text size="xs" style={{ color: '#999' }}>Letzten 6 Monate</Text>
                <div style={{ height: '120px', marginTop: '8px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={[
                      { month: 'Jan', aufrufe: 180 },
                      { month: 'Feb', aufrufe: 220 },
                      { month: 'Mär', aufrufe: 160 },
                      { month: 'Apr', aufrufe: 245 },
                      { month: 'Mai', aufrufe: 210 },
                      { month: 'Jun', aufrufe: 280 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="aufrufe" stroke="#4dabf7" fill="#4dabf7" fillOpacity={0.2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Stack>
            </Card>
          </Stack>
        </Stack>}
      </Stack>
    </div>
  );
}
